import { Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { PageHeaderStatic, WidgetIcon } from '@procureai/ui-mui';

import { NegotiationDomainType } from '~/common/types';
import { DataGridContextProvider } from '~/components/DataGridCustom';
import { DataGridQueryBuilder, useDataGridQueryBuilderQuery } from '~/components/DataGridQueryBuilder';
import { DataGridWidget } from '~/components/DataGridWidget';
import { DomainIcon } from '~/components/DomainIcon/DomainIcon';
import { Filters, FilterSaveStatus, useFilters } from '~/components/Filters';
import { NegotiationPageTitle } from '~/components/NegotiationPageTitle';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { RouteId } from '~/routes';

import { useNegotiationCaseDataGridConfig } from './NegotiationCaseList.dataGrid';
import { NEGOTIATION_CASE_LIST_QUERY } from './NegotiationCaseList.gql';
import { useNegotiationCaseDataGrid } from './NegotiationCaseList.hooks';
import { useNegotiationCaseDataGridQueryBuilderConfig } from './NegotiationCaseList.queryBuilder';

const domain = NegotiationDomainType.NEGOTIATION_CASE;

export const NegotiationCaseList = () => {
  const apiRef = useGridApiRef();
  const { queryId, query, onQueryChange, onQueryIdChange, rulesTotal, loading } = useDataGridQueryBuilderQuery({
    domain,
  });
  const { onFilterSave, saveStatus, selected, ...filtersProps } = useFilters({ domain, queryId });
  const { contextValue } = useNegotiationCaseDataGrid({ apiRef });
  return (
    <PageLayout
      fullHeight
      renderHeader={
        <>
          <PageHeaderStatic
            title={<NegotiationPageTitle domain={domain} />}
            maxWidth={false}
            icon={<WidgetIcon icon={<DomainIcon domain={domain} />} />}
            right={
              <Stack
                spacing={3}
                direction="row"
                sx={{
                  overflow: 'hidden',
                  alignItems: 'center',
                }}
              >
                <Filters
                  {...filtersProps}
                  selected={selected}
                  onFilterSelect={(filter) => {
                    onQueryIdChange(filter.queryId);
                  }}
                />
              </Stack>
            }
          />
          <DataGridQueryBuilder
            query={query}
            clearDisabled={rulesTotal === 0}
            saveDisabled={saveStatus === FilterSaveStatus.DISABLED}
            onQueryChange={onQueryChange}
            onSave={onFilterSave}
            selectedFilterName={selected?.name}
            useConfig={useNegotiationCaseDataGridQueryBuilderConfig}
          />
        </>
      }
    >
      <DataGridContextProvider value={contextValue}>
        <DataGridWidget
          apiRef={apiRef}
          domain={domain}
          loading={loading}
          filter={query}
          filterCount={rulesTotal}
          useConfig={useNegotiationCaseDataGridConfig}
          initialNavigateDetail={({ row }) => ({ to: RouteId.negotiationDetail(row.id) })}
          loadingProps={{
            query: NEGOTIATION_CASE_LIST_QUERY,
            getNodes: (data) => data.tesseract.negotiationCases.nodes,
            getTotalCount: (data) => data.tesseract.negotiationCases.totalCount || 0,
          }}
        />
      </DataGridContextProvider>
    </PageLayout>
  );
};
